import { LoadingSpinner } from "components/LoadingSpinner";
import { orderStatusConstants } from "constants/order";
import { UserRole } from "constants/userRole";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { sizer } from "layout/styles/styled/sizer";
import { RedoOrder } from "pages/CreateOrder/RedoOrder";
import { getPaymentPlanEnabled } from "pages/CreateOrder/utils/helpers";
import { Receipt } from "pages/VerificationPage/components/Receipt";
import { Button } from "primereact/button";
import { useContext, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useGetOrderQuery } from "store/queries/order";
import {
  onSelectOrderToRedo,
  onChangeViewOrderViewReceipt,
  orderSelector,
} from "store/slices/order";
import styled from "styled-components";
import { IOrder } from "types/Order/Order";
import { IPaymentPlan } from "types/Payment";

import { Header } from "./components/OrderHeader/OrderHeader";
import OrderHistory from "./components/OrderHistory/OrderHistory";
import PatientDetails from "./components/PatientDetails/PatientDetails";
import PaymentDetails from "./components/PaymentDetails/PaymentDetails";
import ServiceDetails from "./components/ServiceDetails/ServiceDetails";
import VisitDetails from "./components/VisitDetails/VisitDetails";

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

export function OrderViewPage() {
  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useUserWithAccount();

  const { viewOrderViewReceipt } = useSelector(orderSelector);
  const [redoOrder, setRedoOrder] = useState(false);

  const { data: order, isLoading, error, refetch } = useGetOrderQuery(id);

  const { current: toastElement } = useContext(ToastContext);
  const orderDetails = order?.data as IOrder;

  const showPaymentPlan = useMemo(() => {
    if (!orderDetails) return false;
    const orderStatus = orderDetails?.status;
    const activePaymentPlan = orderDetails.paymentPlan.find((p) => p.active);
    if (
      [
        orderStatusConstants.PAID,
        orderStatusConstants.REFUNDED,
        orderStatusConstants.PARTIALLY_PAID,
        orderStatusConstants.CANCELED,
      ].includes(orderStatus)
    ) {
      // Use paymentPlanBreakdown to determine if this was a payment plan order
      // Payment plan orders always have breakdown data, single payments don't
      return !!orderDetails.paymentPlanBreakdown?.payments?.length;
    }
    return (
      (!!activePaymentPlan && !activePaymentPlan.isDownPaymentOnly) ||
      getPaymentPlanEnabled(
        orderDetails.orderType,
        orderDetails.account.paymentPlanTypeEnabled
      )
    );
  }, [orderDetails]);

  const showDownpaymentOnly = useMemo(() => {
    if (!orderDetails) return false;
    const orderStatus = orderDetails?.status;
    const activeDownpaymentOnly = orderDetails?.paymentPlan?.find(
      (p) => p.active
    )?.isDownPaymentOnly;
    if (activeDownpaymentOnly) {
      return true;
    }
    if (
      orderStatus === orderStatusConstants.SENT_TO_PATIENT &&
      orderDetails?.account?.partialPaymentsEnabled
    ) {
      return true;
    }
    return false;
  }, [orderDetails]);

  const showPaymentPlanEnableButton = useMemo(() => {
    if (!orderDetails) return false;
    const orderStatus = orderDetails?.status;
    if (
      [
        orderStatusConstants.PAID,
        orderStatusConstants.REFUNDED,
        orderStatusConstants.PARTIALLY_PAID,
      ].includes(orderStatus)
    ) {
      return false;
    }
    return true;
  }, [orderDetails]);
  const formInitiator = useForm();

  const paymentPlanEditable = useMemo(() => {
    if (!orderDetails) return false;
    const orderStatus = orderDetails?.status;

    return ![
      orderStatusConstants.CANCELED,
      orderStatusConstants.EXPIRED,
      orderStatusConstants.DELETED,
      orderStatusConstants.REFUNDED,
    ].includes(orderStatus);
  }, [orderDetails]);

  const downpaymentOnlyEditable = useMemo(() => {
    if (!orderDetails) return false;
    const orderStatus = orderDetails?.status;
    if (
      userInfo?.scope === UserRole.SUPER_ADMIN &&
      orderStatus === orderStatusConstants.PAID
    ) {
      return true;
    }
    return orderStatus === orderStatusConstants.SENT_TO_PATIENT;
  }, [orderDetails, userInfo?.scope]);

  // Create a comprehensive key that changes whenever order data changes
  // This ensures PaymentDetails is completely re-rendered on any order update
  const paymentDetailsKey = useMemo(() => {
    if (!orderDetails) return "no-order";

    // Create a key from critical order data that would change on updates
    const keyData = [
      orderDetails.id,
      orderDetails.updatedAt,
      orderDetails.status,
      orderDetails.paymentPlan?.length || 0,
      orderDetails.payments?.length || 0,
      orderDetails.paymentPlanBreakdown?.payments?.length || 0,
      orderDetails.paymentFullBreakdown?.bundledProviderAmounts?.length || 0,
      // Include a simple checksum of provider amounts
      orderDetails.paymentFullBreakdown?.bundledProviderAmounts?.reduce(
        (sum, item) => sum + (item.patientPortion || 0),
        0
      ) || 0,
    ].join("-");

    return `payment-details-${keyData}`;
  }, [orderDetails]);
  // Separate key for OrderHistory so it re-mounts whenever a note is added or
  // the remaining amounts change (paymentDetailsKey misses both of those).
  const orderHistoryKey = useMemo(() => {
    if (!orderDetails) return "no-order";

    const keyData = [
      orderDetails.id,
      orderDetails.updatedAt,
      orderDetails.status,
      orderDetails.orderHistories?.length || 0,
      orderDetails.paymentPlan?.length || 0,
      orderDetails.paymentPlanRemaining?.reduce(
        (sum, item) => sum + (item.remainingAmountInCents || 0),
        0
      ) || 0,
    ].join("-");

    return `order-history-${keyData}`;
  }, [orderDetails]);

  const orderHeaderKey = useMemo(() => {
    if (!orderDetails) return "no-order";

    // Create a key from critical order data that would change on updates
    const keyData = [
      orderDetails.id,
      orderDetails.updatedAt,
      orderDetails.status,
      orderDetails.patient.updatedAt,
      orderDetails.patient.firstName,
      orderDetails.patient.lastName,
      orderDetails.patient.dateOfBirth,
    ].join("-");

    return `header-${keyData}`;
  }, [orderDetails]);

  useEffect(() => {
    dispatch(onChangeViewOrderViewReceipt(false));
  }, [id]);

  const handleSwitchRedoOrder = () => {
    if (!redoOrder) {
      navigate(`/orders/${id}?accountId=${orderDetails.accountId}`);
      dispatch(onSelectOrderToRedo(orderDetails as IOrder));
    } else {
      navigate(`/orders/${id}`);
    }
    setRedoOrder(!redoOrder);
  };

  if (!orderDetails && isLoading) {
    return <LoadingSpinner />;
  }

  if (error && (error as any).status === 403) {
    navigate("/not-found");
    return <LoadingSpinner />;
  }

  if ((error as any)?.originalStatus === 404) {
    toastElement?.show({
      summary: "Error",
      severity: "error",
      detail: "Estimate not found.",
    });
    navigate("/orders");

    return <LoadingSpinner />;
  }

  if (redoOrder) {
    return (
      <RedoOrder
        order={orderDetails}
        handleSwitchRedoOrder={handleSwitchRedoOrder}
      />
    );
  }

  return viewOrderViewReceipt ? (
    <div>
      <StyledButton
        icon="pi pi-arrow-left"
        onClick={() => dispatch(onChangeViewOrderViewReceipt(false))}
        className="p-button-text p-button-secondary p-0"
      >
        <p className="small-text">Return</p>
      </StyledButton>

      <Receipt {...{ order: orderDetails, showBackToOrderButton: false }} />
    </div>
  ) : (
    <div className="flex flex-column gap-2">
      <Header
        {...{
          order: orderDetails,
          onUpdateOrder: () => refetch(),
          orderHistories: orderDetails.orderHistories,
          handleRedoOrder: handleSwitchRedoOrder,
        }}
        key={orderHeaderKey}
      />
      <FormProvider {...formInitiator}>
        <PatientDetails
          {...{ order: orderDetails, onUpdateOrder: () => refetch().unwrap() }}
        />
        <VisitDetails
          {...{ order: orderDetails, onUpdateOrder: () => refetch() }}
        />
        <ServiceDetails
          {...{ order: orderDetails, onUpdateOrder: () => refetch() }}
        />
      </FormProvider>

      <div className="flex gap-3 flex-wrap">
        <FormProvider {...formInitiator}>
          <PaymentDetails
            key={paymentDetailsKey}
            {...{
              payments: orderDetails?.payments,
              paymentPlan: orderDetails?.paymentPlan.find(
                (p) => p.active
              ) as IPaymentPlan,
              orderId: orderDetails.id,
              onUpdateOrder: () => refetch(),
              paymentPlanEditable,
              downpaymentOnlyEditable,
              showPaymentPlan,
              showPaymentPlanEnableButton,
              orderStatus: orderDetails.status,
              account: orderDetails.account,
              paymentPlanBreakDown: orderDetails?.paymentPlanBreakdown,
              orderType: orderDetails.orderType,
              order: orderDetails,
              paymentPlanRemaining: orderDetails?.paymentPlanRemaining,
              showDownpaymentOnly,
            }}
          />
        </FormProvider>
      </div>
      <OrderHistory
        key={orderHistoryKey}
        {...{
          orderHistories: [...orderDetails.orderHistories].sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          ),
          orderCreated: orderDetails.createdAt,
          patientName: `${orderDetails.patient.firstName} ${orderDetails.patient.lastName}`,
          orderId: orderDetails.id,
          onUpdateOrder: () => refetch(),
          orderPaymentNotesEnabled:
            orderDetails.account.features?.orderPaymentNotesEnabled,
          clearPatientFeeValueInPercentage:
            orderDetails.clearPatientFeeValueInPercentage,
          orderStatus: orderDetails.status,
          orderType: orderDetails.orderType,
          providerAmounts:
            orderDetails.paymentFullBreakdown?.bundledProviderAmounts,
          paymentPlanBreakDown: orderDetails?.paymentPlanBreakdown,
          paymentPlan: orderDetails?.paymentPlan?.find((p) => p.active),
          paymentPlanRemaining: orderDetails?.paymentPlanRemaining,
        }}
      />
    </div>
  );
}
